// Taille du texte choisie par l'utilisateur (page Profil) : Normale / Grande /
// Très grande. Posée sur <html data-font-size>, elle fixe la taille racine
// (index.css) ; toutes les tailles de l'interface sont en rem (échelle --fs-*)
// et suivent. Enregistrée côté serveur (PATCH /api/me/preferences, renvoyée par
// /api/me/full) et recopiée en localStorage, que public/theme-init.js relit
// avant le premier rendu pour éviter un saut d'affichage. Même clé que ce script.
export type FontSize = 'normale' | 'grande' | 'tres_grande';

export const FONT_SIZE_STORAGE_KEY = 'jarvis-font-size';

export const FONT_SIZE_OPTIONS: readonly { value: FontSize; label: string }[] = [
  { value: 'normale', label: 'Normale' },
  { value: 'grande', label: 'Grande' },
  { value: 'tres_grande', label: 'Très grande' },
];

/** Taille racine en px pour chaque choix (doit rester alignée sur index.css). */
export const ROOT_PX: Record<FontSize, number> = { normale: 15, grande: 16.5, tres_grande: 18 };

/** Base des conversions px → rem (taille racine « Normale »). */
const BASE_PX = 15;

/** Une longueur pensée en px à la taille Normale, qui suit la taille choisie. */
export function rem(px: number): string {
  return `${+(px / BASE_PX).toFixed(4)}rem`;
}

export function parseFontSize(value: unknown): FontSize | null {
  return value === 'normale' || value === 'grande' || value === 'tres_grande' ? value : null;
}

export function readStoredFontSize(storage: Pick<Storage, 'getItem'> | undefined): FontSize | null {
  try {
    return parseFontSize(storage?.getItem(FONT_SIZE_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function storeFontSize(storage: Pick<Storage, 'setItem'> | undefined, size: FontSize): void {
  try {
    storage?.setItem(FONT_SIZE_STORAGE_KEY, size);
  } catch {
    // Stockage indisponible : le choix vaut pour la page, le serveur le garde.
  }
}

export function currentFontSize(): FontSize {
  return parseFontSize(document.documentElement.getAttribute('data-font-size')) ?? 'normale';
}

function safeLocalStorage(): Storage | undefined {
  try {
    return window.localStorage;
  } catch {
    return undefined;
  }
}

/** Applique la taille sur <html> et la mémorise pour le prochain chargement. */
export function applyFontSize(size: FontSize): void {
  document.documentElement.setAttribute('data-font-size', size);
  storeFontSize(safeLocalStorage(), size);
}
